import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { sendResumeTemplate } from '@/features/negociacaoDetalhes';
import { reabrirNegociacaoCaso } from '@/features/negociacoes';
import { manterSelecionadaNaLista } from '@/shared/utils/manterSelecionadaNaLista';

import { formatarHoraCurta } from '../utils/atendimentoDatas';

import type {
  AtendimentoMensagem,
  AtendimentoTab,
  CentralAtendimentosFiltros,
  CentralAtendimentosSelecao,
  DetalheAtendimento,
  NegociacaoAtendimento,
  NotificacaoAtendimento,
  PaginacaoAtendimentos,
  Responsavel,
} from '../types';
import {
  carregarMaisAtendimentos,
  definirResponsavelAtendimento,
  fetchCentralAtendimentos,
  fetchDetalheAtendimento,
  fetchHistoricoAtendimento,
  revalidarCasoDaNegociacao,
} from './actions';

type CentralAtendimentosStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

interface CentralAtendimentosState {
  responsaveis: Responsavel[];
  currentUserId: string | null;
  negociacoes: NegociacaoAtendimento[];
  notificacoes: NotificacaoAtendimento[];
  status: CentralAtendimentosStatus;
  error: string | null;
  isReabrindo: boolean;
  filtros: CentralAtendimentosFiltros;
  selecao: CentralAtendimentosSelecao;
  paginacao: Record<AtendimentoTab, PaginacaoAtendimentos>;
  // Contagem dos chips vinda do servidor, sobre o filtro inteiro. Null enquanto não chegou.
  totaisPendencia: { com: number; sem: number } | null;
  isCarregandoMais: boolean;
  listDirtySeq: number;
  latestListRequestId: string | null;
  detalheRequestIdPorConversa: Record<string, string | undefined>;
  listaAssinaturaFiltros: string | null;
}

const SEM_PAGINA_SEGUINTE: PaginacaoAtendimentos = { hasMore: false, nextCursor: null };

export const FILTROS_INICIAIS: CentralAtendimentosFiltros = {
  responsavelFiltro: 'todos',
  tipoAtivo: 'negociacoes',
  pendenciaFiltro: 'todos',
  busca: '',
};

const initialState: CentralAtendimentosState = {
  responsaveis: [],
  currentUserId: null,
  negociacoes: [],
  notificacoes: [],
  status: 'idle',
  error: null,
  isReabrindo: false,
  filtros: FILTROS_INICIAIS,
  selecao: { negociacaoId: null, notificacaoId: null },
  paginacao: { negociacoes: SEM_PAGINA_SEGUINTE, notificacoes: SEM_PAGINA_SEGUINTE },
  totaisPendencia: null,
  isCarregandoMais: false,
  listDirtySeq: 0,
  latestListRequestId: null,
  detalheRequestIdPorConversa: {},
  listaAssinaturaFiltros: null,
};

interface EnviarMensagemPayload {
  id: string;
  mensagemId: string;
  texto: string;
  autorId: string | null;
  autorNome: string | null;
}

// A lista (`GET /conversas`) não devolve mensagens, situações nem os campos do detalhe — o mapper
// preenche vazio. Sobrescrever com ele apagaria a thread aberta a cada revalidação.
const mesclarHidratacao = <T extends { id: string }>(
  atuais: T[],
  recebidas: T[],
  hidratar: (recebida: T, atual: T) => T,
): T[] => {
  const porId = new Map(atuais.map((item) => [item.id, item]));
  return recebidas.map((recebida) => {
    const atual = porId.get(recebida.id);
    return atual ? hidratar(recebida, atual) : recebida;
  });
};

const assinaturaFiltros = (filtros: CentralAtendimentosFiltros): string =>
  [filtros.responsavelFiltro, filtros.pendenciaFiltro, filtros.busca.trim()].join('|');

const mesclarRevalidacaoComExtras = <T extends { id: string }>(
  atuais: T[],
  pagina1: T[],
  hidratar: (recebida: T, atual: T) => T,
): { lista: T[]; manteveExtras: boolean } => {
  const mesclada = mesclarHidratacao(atuais, pagina1, hidratar);
  const idsPagina1 = new Set(pagina1.map((item) => item.id));
  const extras = atuais.filter((item) => !idsPagina1.has(item.id));
  if (extras.length === 0) return { lista: mesclada, manteveExtras: false };
  return { lista: [...mesclada, ...extras], manteveExtras: true };
};

// O histórico é mais recente que o preview da lista: o realtime recarrega a thread aberta a cada
// evento, enquanto a lista só revalida no refetch. Texto vazio (mídia) não sobrescreve o rótulo
// que o servidor manda para a lista.
const aplicarPreviewDaThread = (
  atendimento: { previewTexto: string; previewHora: string },
  mensagens: AtendimentoMensagem[],
): void => {
  const ultima = mensagens.at(-1);
  if (!ultima?.texto) return;
  atendimento.previewTexto = ultima.texto;
  // `hora` da thread carrega a data; na linha da lista cabe só a hora.
  if (ultima.dataMensagem) atendimento.previewHora = formatarHoraCurta(ultima.dataMensagem);
};

// Seleção, tempo real, polling e janela expirando consultam o caso em paralelo. Só a última
// consulta disparada para a conversa grava os campos do caso: uma resposta atrasada devolveria a
// janela (que trava o campo de mensagem) e a oferta de antes.
const consumirSeMaisRecente = (
  state: CentralAtendimentosState,
  conversaId: string,
  requestId: string,
): boolean => {
  if (state.detalheRequestIdPorConversa[conversaId] !== requestId) return false;
  delete state.detalheRequestIdPorConversa[conversaId];
  return true;
};

const aplicarResumoDoCaso = (
  negociacao: NegociacaoAtendimento,
  resumo: Pick<DetalheAtendimento, 'valor' | 'situacoes' | 'janelaAtendimento' | 'casoParaOferta'>,
): void => {
  negociacao.valorEmAberto = resumo.valor;
  negociacao.situacoes = resumo.situacoes;
  negociacao.casoParaOferta = resumo.casoParaOferta ?? null;
  if (resumo.janelaAtendimento !== undefined) {
    negociacao.janelaAtendimento = resumo.janelaAtendimento;
  }
};

const ocultarOfertaSeMaisRecente = (
  state: CentralAtendimentosState,
  conversaId: string,
  requestId: string,
): void => {
  if (!consumirSeMaisRecente(state, conversaId, requestId)) return;
  const negociacao = state.negociacoes.find((item) => item.id === conversaId);
  if (negociacao) negociacao.casoParaOferta = null;
};

const buildMensagem = (payload: EnviarMensagemPayload): AtendimentoMensagem => ({
  id: payload.mensagemId,
  remetente: 'atendente',
  texto: payload.texto,
  dataMensagem: '',
  hora: 'agora',
  tipo: 'TEXT',
  status: 'PENDING',
  mediaUrl: null,
  autorId: payload.autorId ?? undefined,
  autorNome: payload.autorNome ?? undefined,
});

const centralAtendimentosSlice = createSlice({
  name: 'centralAtendimentos',
  initialState,
  reducers: {
    aplicarFiltros: (state, action: PayloadAction<Partial<CentralAtendimentosFiltros>>) => {
      state.filtros = { ...state.filtros, ...action.payload };
    },
    selecionarAtendimento: (
      state,
      action: PayloadAction<{ tipo: AtendimentoTab; id: string | null }>,
    ) => {
      const { tipo, id } = action.payload;
      state.selecao =
        tipo === 'negociacoes'
          ? { negociacaoId: id, notificacaoId: null }
          : { negociacaoId: null, notificacaoId: id };
    },
    marcarListaSuja: (state) => {
      state.listDirtySeq += 1;
    },
    // Trocar de workspace troca o tenant dos atendimentos: manter a lista anterior em tela oferece
    // itens que o detalhe recusa (404), porque o header já é do workspace novo.
    limparAtendimentosDoWorkspace: (state) => {
      state.detalheRequestIdPorConversa = {};
      state.negociacoes = [];
      state.notificacoes = [];
      state.responsaveis = [];
      state.selecao = { negociacaoId: null, notificacaoId: null };
      state.paginacao = {
        negociacoes: SEM_PAGINA_SEGUINTE,
        notificacoes: SEM_PAGINA_SEGUINTE,
      };
      state.totaisPendencia = null;
      state.listaAssinaturaFiltros = null;
      state.status = 'loading';
    },
    markNegociacaoLida: (state, action: PayloadAction<string>) => {
      const negociacao = state.negociacoes.find((item) => item.id === action.payload);
      if (negociacao) negociacao.naoLida = false;
    },
    markNotificacaoLida: (state, action: PayloadAction<string>) => {
      const notificacao = state.notificacoes.find((item) => item.id === action.payload);
      if (notificacao) notificacao.naoLida = false;
    },
    toggleNegociacaoAutomacao: (state, action: PayloadAction<string>) => {
      const negociacao = state.negociacoes.find((item) => item.id === action.payload);
      if (!negociacao) return;
      negociacao.iaPausada = !negociacao.iaPausada;
    },
    enviarMensagemNegociacao: (state, action: PayloadAction<EnviarMensagemPayload>) => {
      const negociacao = state.negociacoes.find((item) => item.id === action.payload.id);
      if (!negociacao || negociacao.encerrada) return;
      negociacao.iaPausada = true;
      negociacao.retomadaAmbigua = false;
      const mensagem = buildMensagem(action.payload);
      negociacao.mensagens.push(mensagem);
      negociacao.previewTexto = mensagem.texto;
      negociacao.previewHora = mensagem.hora;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCentralAtendimentos.pending, (state, action) => {
        // Revalidação em segundo plano (foco na janela, poll, realtime) não pode devolver a tela
        // ao esqueleto: o layout inteiro desmonta e leva junto a conversa aberta.
        if (state.status !== 'succeeded') state.status = 'loading';
        state.error = null;
        state.latestListRequestId = action.meta.requestId;
      })
      .addCase(fetchCentralAtendimentos.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.latestListRequestId) return;
        state.latestListRequestId = null;
        state.status = 'succeeded';
        state.responsaveis = action.payload.responsaveis;
        state.currentUserId = action.payload.currentUserId;

        const filtrosArg = action.meta.arg as CentralAtendimentosFiltros | undefined;
        const assinatura = filtrosArg ? assinaturaFiltros(filtrosArg) : null;
        const revalidacaoMesmosFiltros =
          assinatura !== null &&
          state.listaAssinaturaFiltros !== null &&
          assinatura === state.listaAssinaturaFiltros;

        const hidratarNeg = (
          recebida: NegociacaoAtendimento,
          atual: NegociacaoAtendimento,
        ): NegociacaoAtendimento => ({
          ...recebida,
          mensagens: atual.mensagens,
          cpfCnpj: atual.cpfCnpj,
          valorEmAberto: atual.valorEmAberto,
          situacoes: atual.situacoes,
          casoParaOferta: atual.casoParaOferta,
          janelaAtendimento: atual.janelaAtendimento,
        });
        const hidratarNotif = (
          recebida: NotificacaoAtendimento,
          atual: NotificacaoAtendimento,
        ): NotificacaoAtendimento => ({
          ...recebida,
          manifestacao: atual.manifestacao,
          mensagensEnviadas: atual.mensagensEnviadas,
          cpfCnpj: atual.cpfCnpj,
          valor: atual.valor,
          status: atual.status,
        });

        // O payload traz UMA aba (só a ativa é buscada). Mexer na outra aqui apagaria a lista que
        // ela já tem em memória, sem nada para pôr no lugar.
        const tab = action.payload.tab;
        state.totaisPendencia = action.payload.totaisPendencia;

        if (tab === 'negociacoes') {
          if (revalidacaoMesmosFiltros) {
            const negs = mesclarRevalidacaoComExtras(
              state.negociacoes,
              action.payload.negociacoes,
              hidratarNeg,
            );
            state.negociacoes = manterSelecionadaNaLista(
              state.negociacoes,
              negs.lista,
              state.selecao.negociacaoId,
            );
            if (!negs.manteveExtras) state.paginacao.negociacoes = action.payload.paginacao;
          } else {
            state.negociacoes = manterSelecionadaNaLista(
              state.negociacoes,
              mesclarHidratacao(state.negociacoes, action.payload.negociacoes, hidratarNeg),
              state.selecao.negociacaoId,
            );
            state.paginacao.negociacoes = action.payload.paginacao;
          }
        } else if (revalidacaoMesmosFiltros) {
          const notifs = mesclarRevalidacaoComExtras(
            state.notificacoes,
            action.payload.notificacoes,
            hidratarNotif,
          );
          state.notificacoes = manterSelecionadaNaLista(
            state.notificacoes,
            notifs.lista,
            state.selecao.notificacaoId,
          );
          if (!notifs.manteveExtras) state.paginacao.notificacoes = action.payload.paginacao;
        } else {
          state.notificacoes = manterSelecionadaNaLista(
            state.notificacoes,
            mesclarHidratacao(state.notificacoes, action.payload.notificacoes, hidratarNotif),
            state.selecao.notificacaoId,
          );
          state.paginacao.notificacoes = action.payload.paginacao;
        }

        if (assinatura !== null) state.listaAssinaturaFiltros = assinatura;
      })
      .addCase(carregarMaisAtendimentos.pending, (state) => {
        state.isCarregandoMais = true;
      })
      .addCase(carregarMaisAtendimentos.fulfilled, (state, action) => {
        state.isCarregandoMais = false;
        const { tipo, paginacao } = action.payload;
        // A lista pode ter sido trocada por um refetch de filtro enquanto a página vinha: aí o
        // cursor que pedimos não é mais o da lista em tela, e anexar misturaria dois filtros.
        if (action.payload.cursorUsado !== state.paginacao[tipo].nextCursor) return;
        // Append por id: a página seguinte pode repetir item que o realtime já trouxe para cá.
        if (tipo === 'negociacoes') {
          const presentes = new Set(state.negociacoes.map((item) => item.id));
          state.negociacoes.push(
            ...action.payload.negociacoes.filter((item) => !presentes.has(item.id)),
          );
        } else {
          const presentes = new Set(state.notificacoes.map((item) => item.id));
          state.notificacoes.push(
            ...action.payload.notificacoes.filter((item) => !presentes.has(item.id)),
          );
        }
        state.paginacao[tipo] = paginacao;
      })
      .addCase(carregarMaisAtendimentos.rejected, (state) => {
        state.isCarregandoMais = false;
      })
      .addCase(fetchCentralAtendimentos.rejected, (state, action) => {
        if (action.meta.requestId !== state.latestListRequestId) return;
        state.latestListRequestId = null;
        state.error = action.payload ?? 'Não foi possível carregar a central de atendimentos.';
        // Falha de revalidação mantém a lista carregada em tela; só a primeira carga vira erro.
        if (state.status !== 'succeeded') state.status = 'failed';
      })
      .addCase(fetchHistoricoAtendimento.fulfilled, (state, action) => {
        const { id, tipo, mensagens } = action.payload;
        if (tipo === 'negociacoes') {
          const negociacao = state.negociacoes.find((item) => item.id === id);
          if (negociacao) {
            negociacao.mensagens = mensagens;
            aplicarPreviewDaThread(negociacao, mensagens);
          }
          return;
        }
        const notificacao = state.notificacoes.find((item) => item.id === id);
        if (notificacao) {
          notificacao.manifestacao = mensagens;
          notificacao.mensagensEnviadas = mensagens.filter(
            (mensagem) => mensagem.remetente === 'atendente',
          ).length;
          aplicarPreviewDaThread(notificacao, mensagens);
        }
      })
      .addCase(definirResponsavelAtendimento.fulfilled, (state, action) => {
        const { id, tipo, responsavelId } = action.payload;
        const lista = tipo === 'negociacoes' ? state.negociacoes : state.notificacoes;
        const item = lista.find((atendimento) => atendimento.id === id);
        if (item) item.responsavelId = responsavelId;
      })
      .addCase(fetchDetalheAtendimento.pending, (state, action) => {
        const { id, tipo } = action.meta.arg;
        if (tipo !== 'negociacoes') return;
        state.detalheRequestIdPorConversa[id] = action.meta.requestId;
        // Esta é a consulta da seleção. O snapshot de quando a conversa foi aberta antes pode estar
        // velho (notificação gerada, atendimento assumido em outra aba) e não autoriza a oferta.
        const negociacao = state.negociacoes.find((item) => item.id === id);
        if (negociacao) negociacao.casoParaOferta = null;
      })
      .addCase(fetchDetalheAtendimento.rejected, (state, action) => {
        const { id, tipo } = action.meta.arg;
        if (tipo !== 'negociacoes') return;
        ocultarOfertaSeMaisRecente(state, id, action.meta.requestId);
      })
      .addCase(fetchDetalheAtendimento.fulfilled, (state, action) => {
        const { id, tipo, cpfCnpj, valor, status } = action.payload;
        if (tipo === 'negociacoes') {
          const maisRecente = consumirSeMaisRecente(state, id, action.meta.requestId);
          const negociacao = state.negociacoes.find((item) => item.id === id);
          if (!negociacao) return;
          // CPF/CNPJ é da conversa e não muda entre consultas; os campos do caso, sim.
          negociacao.cpfCnpj = cpfCnpj;
          if (maisRecente) aplicarResumoDoCaso(negociacao, action.payload);
          return;
        }
        const notificacao = state.notificacoes.find((item) => item.id === id);
        if (notificacao) {
          notificacao.cpfCnpj = cpfCnpj;
          notificacao.valor = valor;
          if (status) notificacao.status = status;
        }
      })
      .addCase(revalidarCasoDaNegociacao.pending, (state, action) => {
        state.detalheRequestIdPorConversa[action.meta.arg] = action.meta.requestId;
      })
      .addCase(revalidarCasoDaNegociacao.rejected, (state, action) => {
        ocultarOfertaSeMaisRecente(state, action.meta.arg, action.meta.requestId);
      })
      .addCase(revalidarCasoDaNegociacao.fulfilled, (state, action) => {
        const { id } = action.payload;
        if (!consumirSeMaisRecente(state, id, action.meta.requestId)) return;
        const negociacao = state.negociacoes.find((item) => item.id === id);
        if (negociacao) aplicarResumoDoCaso(negociacao, action.payload);
      })
      .addCase(sendResumeTemplate.fulfilled, (state, action) => {
        const negociacao = state.negociacoes.find((item) => item.casoId === action.payload.caseId);
        if (negociacao) negociacao.janelaAtendimento = action.payload.janelaAtendimento;
      })
      .addCase(reabrirNegociacaoCaso.pending, (state) => {
        state.isReabrindo = true;
      })
      .addCase(reabrirNegociacaoCaso.fulfilled, (state, action) => {
        state.isReabrindo = false;
        // Toda conversa do caso, não só a primeira da lista: com dois telefones, a selecionada podia
        // seguir "encerrada" depois de reaberta. E nenhuma resposta de antes restaura a oferta.
        for (const item of state.negociacoes) {
          if (item.casoId !== action.payload.caseId) continue;
          item.encerrada = false;
          item.iaPausada = false;
          item.pendente = false;
          item.retomadaAmbigua = false;
          item.casoParaOferta = null;
          delete state.detalheRequestIdPorConversa[item.id];
        }
      })
      .addCase(reabrirNegociacaoCaso.rejected, (state) => {
        state.isReabrindo = false;
      });
  },
});

export const {
  aplicarFiltros,
  marcarListaSuja,
  limparAtendimentosDoWorkspace,
  selecionarAtendimento,
  markNegociacaoLida,
  markNotificacaoLida,
  toggleNegociacaoAutomacao,
  enviarMensagemNegociacao,
} = centralAtendimentosSlice.actions;

export const centralAtendimentosReducer = centralAtendimentosSlice.reducer;
